import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReportBuilderLayout from "../../components/ReportBuilderLayout";
import { FormControl, InputLabel } from "@mui/material";
import { DateTime } from "luxon";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { CustomDateInput } from "../../components/DatePickerWithManualEntry";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import { DbRead } from "../../database";
import { useUserDisplayNamesStore } from "../../store/user-display-names-map";
import { useToastMessageStore } from "../../store/toast-messages";
import { createToastMessageID } from "../../utils";
import * as strings from "../../strings";
import {
  groupKpiDataByDay,
  groupKpiDataByMonth,
  groupKpiDataByWeek,
  KpiDataPoint,
} from "../../utils/kpiUtils";
import KpiBarChart from "../../components/KPIs/BarChart";
import KpiDataTable, {
  KPITableViewMode,
} from "../../components/KPIs/DataTable";
import {
  getDefaultDateRange,
  initialUserKPIState,
  KPIBaseProps,
  UserKPIState,
} from "../../components/KPIs/KPICommon";
import moment from "moment";
import { KpiDocument, KpiSpec } from "../../models/kpis";
import { getKpiValueWithFormatting } from "../../utils/kpiUtils";
import { useSiteKeyUsersStore } from "../../store/site-key-users";
import SearchableDropdown from "../../components/SearchableDropdown";

// Add these helper functions at the top of the file, outside the component
function transformDataForTableDateView(chartData: any[]): KpiDataPoint[] {
  // Create a map of dates to their data points
  const dateMap = new Map<string, any>();

  chartData.forEach((chart) => {
    chart.data.forEach((dataPoint: any) => {
      if (!dateMap.has(dataPoint.date)) {
        dateMap.set(dataPoint.date, {
          date: dataPoint.date,
          _sortKey: dataPoint._sortKey || dataPoint.date, // Preserve the _sortKey
        });
      }

      const existingPoint = dateMap.get(dataPoint.date);
      // Get the raw value
      const rawValue =
        chart.data.find((d: any) => d.date === dataPoint.date)?.[
          chart.categories[0]
        ] || 0;
      // Format the value based on the KPI type's formatting
      existingPoint[chart.title] = getKpiValueWithFormatting(
        chart.formatting,
        Number(rawValue),
      );
    });
  });

  return Array.from(dateMap.values());
}

function transformDataForTableCategoryView(
  rawData: Record<string, KpiDataPoint[]>,
  selectedUserId: string,
  selectedUserDisplayName: string,
  kpiSpecs: Record<string, KpiSpec>,
): KpiDataPoint[] {
  const output: any[] = [];

  // categories is an array of userIDs, taskTypes, craftTypes, or other raw values
  Object.entries(rawData).forEach(([kpiType, data]) => {
    const kpiSpec = kpiSpecs[kpiType];
    const categorySums: Record<string, any> = {};
    for (const row of data) {
      if (row[selectedUserId]) {
        if (
          // @ts-ignore
          typeof row[selectedUserId]["numerator"] === "number" &&
          // @ts-ignore
          typeof row[selectedUserId]["denominator"] === "number"
        ) {
          const rowCategory = row[selectedUserId] as Record<string, number>;
          if (!categorySums[selectedUserId]) {
            categorySums[selectedUserId] = {
              numerator: rowCategory["numerator"],
              denominator: rowCategory["denominator"],
            };
          } else {
            categorySums[selectedUserId]["numerator"] +=
              rowCategory["numerator"];
            categorySums[selectedUserId]["denominator"] +=
              rowCategory["denominator"];
          }
        } else {
          // if categorySums has this categoryName already, then add the new sum to it, otherwise set it to the new sum
          if (!categorySums[selectedUserId]) {
            categorySums[selectedUserId] = Number(row[selectedUserId]);
          } else {
            categorySums[selectedUserId] += Number(row[selectedUserId]);
          }
        }
      }
    }

    // output should be in format of [{category: "name", amount: 12}]
    for (const [category, amount] of Object.entries(categorySums)) {
      let rawAmount: number;
      if (typeof amount === "object") {
        rawAmount = 0;
        if (amount["denominator"] !== 0) {
          rawAmount = amount["numerator"] / amount["denominator"];
        }
      } else {
        rawAmount = amount;
      }

      // Format the value based on the KPI type's formatting
      const formattedAmount = getKpiValueWithFormatting(
        kpiSpec.formatting,
        rawAmount,
      );

      output.push({
        date: kpiSpec.header,
        [category]: formattedAmount,
        amount: formattedAmount,
      });
    }
  });

  return output;
}

export default function UserKPIs({ siteKey }: KPIBaseProps) {
  const addMessage = useToastMessageStore((state) => state.addToastMessage);
  const userDisplayNamesMap = useUserDisplayNamesStore(
    (state) => state.userDisplayNames,
  );
  const siteKeyUsersList = useSiteKeyUsersStore(
    (state) => state.siteKeyUsersList,
  );
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [tableViewMode, setTableViewMode] = useState<KPITableViewMode>("date");
  const [state, setState] = useState<UserKPIState>(initialUserKPIState);

  // URL params handling
  const updateUrlSearchParams = useCallback(
    (newParams: Record<string, string>) => {
      setUrlSearchParams(
        (prev) => {
          const prevParams: Record<string, string> = {};
          prev.forEach((value, key) => {
            prevParams[key] = value;
          });
          return { ...prevParams, ...newParams };
        },
        { replace: true },
      );
    },
    [setUrlSearchParams],
  );

  // Get date range from URL or defaults
  const UrlStartDate = urlSearchParams.get("startDate");
  const UrlEndDate = urlSearchParams.get("endDate");
  const defaultDates = getDefaultDateRange();
  const startDate = UrlStartDate || defaultDates.startDate;
  const endDate = UrlEndDate || defaultDates.endDate;

  // Fetch KPI specs on mount
  useEffect(() => {
    async function getKpiSpecs() {
      setState((prev) => ({ ...prev, isFetchingKpiSpecs: true }));
      try {
        const kpiSpecs = await DbRead.kpisv2.getKpiSpecs(siteKey);
        // Filter for only user-based KPIs
        const userKpiSpecs = Object.entries(kpiSpecs).reduce(
          (acc, [key, spec]) => {
            if (spec.categoryType === "userID") {
              acc[key] = spec;
            }
            return acc;
          },
          {} as Record<string, KpiSpec>,
        );

        setState((prev) => ({
          ...prev,
          kpiSpecs: userKpiSpecs,
          isFetchingKpiSpecs: false,
        }));
      } catch (e) {
        addMessage({
          id: createToastMessageID(),
          message: strings.ERROR_FETCHING_DATA,
          dialog: false,
          type: "error",
        });
        setState((prev) => ({ ...prev, isFetchingKpiSpecs: false }));
      }
    }
    getKpiSpecs();
  }, [siteKey, addMessage]);

  // Fetch data for selected user
  const fetchUserKpiData = useCallback(async () => {
    if (!selectedUserId) {
      addMessage({
        id: createToastMessageID(),
        message: "Please select a user",
        dialog: false,
        type: "error",
      });
      return;
    }

    setState((prev) => ({ ...prev, isFetchingData: true }));
    try {
      const promises = Object.entries(state.kpiSpecs).map(([kpiType, spec]) =>
        DbRead.kpisv2.getAllInDateRange(siteKey, kpiType, startDate, endDate),
      );

      const results = await Promise.all(promises);
      const allDocs = results.flat();

      if (allDocs.length > 0) {
        // Set the firestore data as a map of kpiType to KpiDocument[]
        const firestoreData: Record<string, KpiDocument[]> = {};
        allDocs.forEach((doc) => {
          if (!firestoreData[doc.kpiType]) {
            firestoreData[doc.kpiType] = [];
          }
          firestoreData[doc.kpiType].push(doc);
        });

        setState((prev) => ({
          ...prev,
          firestoreData,
          isFetchingData: false,
        }));
      }
    } catch (error) {
      addMessage({
        id: createToastMessageID(),
        message: strings.ERROR_FETCHING_DATA,
        dialog: false,
        type: "error",
      });
      setState((prev) => ({ ...prev, isFetchingData: false }));
    }
  }, [selectedUserId, state.kpiSpecs, siteKey, startDate, endDate, addMessage]);

  // Process data when firestore data changes
  useEffect(() => {
    if (!state.firestoreData) return;

    // Loop through the firestore data and filter for selected user
    for (const [kpiType, docs] of Object.entries(state.firestoreData)) {
      let allData: KpiDataPoint[] = [];
      for (const doc of docs) {
        allData = allData.concat(doc.data as KpiDataPoint[]);
      }

      // Sort and filter by date
      allData = allData
        .sort((a, b) => a.date.localeCompare(b.date))
        .filter((data) => {
          return (
            moment(data.date).isSameOrAfter(moment(startDate)) &&
            moment(data.date).isSameOrBefore(endDate)
          );
        });

      state.rawData[kpiType] = allData;
      groupTheData();
    }
  }, [state.firestoreData, selectedUserId, startDate, endDate]);
  // alex FIXME: dependencies

  function groupTheData() {
    if (Object.keys(state.rawData).length === 0) {
      return;
    }
    state.chartData = [];
    // Loop through the raw data and group it by the selected date range
    for (const [kpiType, rawData] of Object.entries(state.rawData)) {
      if (!state.kpiSpecs[kpiType]) {
        continue;
      }
      if (state.groupByDate === "week") {
        const groupedData = groupKpiDataByWeek(rawData, false).map((d) => ({
          ...d,
          _sortKey: moment(d.date, "YYYY-[W]WW").format("YYYY-MM-DD"),
        }));
        state.chartData.push({
          kpiType,
          categoryType: "userID",
          categories: [selectedUserId],
          data: groupedData,
          title: state.kpiSpecs[kpiType].header,
          subTitle: state.kpiSpecs[kpiType].subheader,
          formatting: state.kpiSpecs[kpiType].formatting,
          groupByDate: state.groupByDate,
          handleGroupByDateChange: (value) =>
            setState((prev) => ({
              ...prev,
              groupByDate: value as "month" | "week" | "day",
            })),
          stacked: false,
        });
      } else if (state.groupByDate === "day") {
        const groupedData = groupKpiDataByDay(
          rawData,
          startDate,
          endDate,
          false,
        ).map((d) => ({
          ...d,
          _sortKey: d.date, // For daily data, date is already in YYYY-MM-DD format
        }));
        state.chartData.push({
          kpiType,
          categoryType: "userID",
          categories: [selectedUserId],
          data: groupedData,
          title: state.kpiSpecs[kpiType].header,
          subTitle: state.kpiSpecs[kpiType].subheader,
          formatting: state.kpiSpecs[kpiType].formatting,
          groupByDate: state.groupByDate,
          handleGroupByDateChange: (value) =>
            setState((prev) => ({
              ...prev,
              groupByDate: value as "month" | "week" | "day",
            })),
          stacked: false,
        });
      } else {
        const groupedData = groupKpiDataByMonth(
          rawData,
          startDate,
          endDate,
          false,
        ).map((d) => ({
          ...d,
          _sortKey: moment(d.date, "MMMM YYYY").format("YYYY-MM-DD"),
        }));
        state.chartData.push({
          kpiType,
          categoryType: "userID",
          categories: [selectedUserId],
          data: groupedData,
          title: state.kpiSpecs[kpiType].header,
          subTitle: state.kpiSpecs[kpiType].subheader,
          formatting: state.kpiSpecs[kpiType].formatting,
          groupByDate: state.groupByDate,
          handleGroupByDateChange: (value) =>
            setState((prev) => ({
              ...prev,
              groupByDate: value as "month" | "week" | "day",
            })),
          stacked: false,
        });
      }
    }

    const dateViewData = transformDataForTableDateView(state.chartData);
    const selectedUserDisplayName = userDisplayNamesMap[selectedUserId];
    const categoryViewData = transformDataForTableCategoryView(
      state.rawData,
      selectedUserId,
      selectedUserDisplayName,
      state.kpiSpecs,
    );

    setState((prev) => ({
      ...prev,
      tableDataByDate: dateViewData,
      tableDataByCategory: categoryViewData,
      categories: state.chartData.map((chart) => chart.title),
      kpiCategoryType: "userID",
    }));
  }

  useEffect(() => {
    if (!state.rawData) {
      return;
    }
    groupTheData();
  }, [endDate, state.groupByDate, startDate]);
  // TODO: ^ alex FIXME: dependency list ... then wrap groupTheData in useCallback..
  // sometime when you're not...gazzed. 💎

  // Get user options from site key users list
  const userOptions = useMemo(() => {
    return siteKeyUsersList
      .flatMap((user) => {
        if (user.systemUser) return []; // filter out system users
        return {
          id: user.id,
          name: user.displayName,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [siteKeyUsersList]);

  return (
    <ReportBuilderLayout title="User KPI Dashboard">
      {{
        tableControlsSlot: (
          <div className="mt-8 flex flex-row justify-start gap-4 md:items-center">
            <div className="flex flex-col items-center gap-4 md:flex-row">
              {/* User Select */}
              <div className="-mt-1 w-72">
                <SearchableDropdown
                  items={userOptions.map((user) => ({
                    id: user.id,
                    title: user.name,
                  }))}
                  value={selectedUserId}
                  onChange={(ev) => setSelectedUserId(ev)}
                  placeholder="Select User"
                />
              </div>

              <div className="-mb-1 flex gap-4">
                {/* Date Controls */}
                <FormControl sx={{ maxWidth: 140 }}>
                  <InputLabel id="start-date-label">Start Date</InputLabel>
                  <DatePicker
                    selected={DateTime.fromISO(startDate).toJSDate()}
                    onChange={(date) => {
                      const newDate = date
                        ? DateTime.fromJSDate(date).toISODate()
                        : "";
                      updateUrlSearchParams({ startDate: newDate });
                    }}
                    customInput={
                      <CustomDateInput id="start-date" className="pl-3 pt-1" />
                    }
                  />
                </FormControl>
                <FormControl sx={{ maxWidth: 140 }}>
                  <InputLabel id="end-date-label">End Date</InputLabel>
                  <DatePicker
                    selected={DateTime.fromISO(endDate).toJSDate()}
                    onChange={(date) => {
                      const newDate = date
                        ? DateTime.fromJSDate(date).toISODate()
                        : "";
                      updateUrlSearchParams({ endDate: newDate });
                    }}
                    customInput={
                      <CustomDateInput id="end-date" className="pl-3 pt-1" />
                    }
                  />
                </FormControl>
              </div>
            </div>

            {/* Submit Button */}
            <BaseButtonPrimary
              isBusy={state.isFetchingData}
              busyText={strings.buttons.BUSY_LOADING}
              onClick={fetchUserKpiData}
            >
              {strings.buttons.SUBMIT}
            </BaseButtonPrimary>
          </div>
        ),
        tableSlot: (
          <>
            {/* Data Table */}
            <div className="mb-6 md:col-span-2">
              <KpiDataTable
                title={userDisplayNamesMap[selectedUserId]}
                userKPIs={true}
                setTableViewMode={setTableViewMode}
                tableViewMode={tableViewMode}
                data={
                  tableViewMode === "date"
                    ? state.tableDataByDate
                    : state.tableDataByCategory
                }
                categories={state.categories}
                formatting="raw"
                categoryType={state.kpiCategoryType}
                userDisplayNamesMap={userDisplayNamesMap}
                kpiTitle="User KPI Dashboard"
                dateRange={{ startDate, endDate }}
              />
            </div>
            {state.chartData.length > 0 && (
              <>
                {/* Bar Charts */}
                {state.chartData.map((chartData) => {
                  return (
                    <KpiBarChart
                      key={`${chartData.kpiType}-${chartData.groupByDate.length ?? 0}`}
                      categoryType={chartData.categoryType}
                      categories={[selectedUserId]}
                      data={chartData.data}
                      title={chartData.title}
                      subTitle={chartData.subTitle}
                      formatting={chartData.formatting}
                      groupByDate={chartData.groupByDate}
                      handleGroupByDateChange={(value) =>
                        setState((prev) => ({
                          ...prev,
                          groupByDate: value as "month" | "week" | "day",
                        }))
                      }
                      stacked={false}
                      kpiTypes={[chartData.kpiType]}
                    />
                  );
                })}
              </>
            )}
          </>
        ),
      }}
    </ReportBuilderLayout>
  );
}
